import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  FlaskConical, Play, Square, AlertTriangle, Activity, Radio, Clock, Download,
  Eye, Trash2, RotateCcw, Plus, Loader2, Wifi, WifiOff, CheckCircle, XCircle,
  Tag, BarChart3, Target, Zap, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, Area, ComposedChart, Legend, Bar, BarChart, Cell
} from 'recharts';

import GuidedTestWizard from '@/components/GuidedTestWizard';
import TestLabAnalysis from '@/components/testlab/TestLabAnalysis';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'unknown', label: 'Okand' }, { value: 'normal', label: 'Normal luft' },
  { value: 'vape', label: 'Vape' }, { value: 'spray', label: 'Spray/Deodorant' },
  { value: 'mixed', label: 'Blandat (Vape+Spray)' }, { value: 'steam', label: 'Anga' },
  { value: 'other', label: 'Annat' },
];

const GROUND_TRUTH_LABELS = [
  { value: 'normal', label: 'Normal', color: '#22c55e' },
  { value: 'vape', label: 'Vape', color: '#ef4444' },
  { value: 'spray', label: 'Spray', color: '#f59e0b' },
  { value: 'mixed', label: 'Blandat', color: '#a855f7' },
  { value: 'steam', label: 'Anga', color: '#3b82f6' },
  { value: 'other', label: 'Annat', color: '#6b7280' },
  { value: 'invalid', label: 'Ogiltigt test', color: '#94a3b8' },
];

const MARKER_TYPES = [
  { value: 'vape_now', label: 'VAPE NU', color: '#ef4444', icon: '!' },
  { value: 'spray_now', label: 'SPRAY NU', color: '#f59e0b', icon: 'S' },
  { value: 'steam_now', label: 'ANGA NU', color: '#3b82f6', icon: '~' },
  { value: 'other_event', label: 'HANDELSE', color: '#a855f7', icon: '?' },
  { value: 'door_open', label: 'Dorr oppen', color: '#64748b', icon: 'D' },
  { value: 'door_closed', label: 'Dorr stangd', color: '#64748b', icon: 'D' },
  { value: 'note', label: 'Anteckning', color: '#94a3b8', icon: 'N' },
];

const STATUS_MAP = {
  created: { label: 'Skapad', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  recording: { label: 'Spelar in', cls: 'bg-red-100 text-red-700 border-red-200 animate-pulse' },
  stopping: { label: 'Stoppar...', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Klar', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed: { label: 'Misslyckad', cls: 'bg-red-100 text-red-700 border-red-200' },
  aborted: { label: 'Avbruten', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

const VERDICT_COLORS = { TP: '#22c55e', FP: '#ef4444', TN: '#3b82f6', FN: '#f59e0b' };

export default function TestLabPage() {
  const { token } = useAuth();
  const { subscribe, subscribeToDevice, unsubscribeFromDevice } = useWebSocket();
  const [view, setView] = useState('sessions'); // sessions | live | detail | validation | guided
  const [sessions, setSessions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showNewTest, setShowNewTest] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);
  const [stopping, setStopping] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [detailReadings, setDetailReadings] = useState([]);
  const [replayData, setReplayData] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [validationSplit, setValidationSplit] = useState('all');
  const [validationSource, setValidationSource] = useState('live');
  const [replayLoading, setReplayLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const pollRef = useRef(null);
  const h = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const [form, setForm] = useState({
    device_id: '', name: '', category: 'unknown', substance: '', distance_m: 1.0,
    ventilation: 'unknown', door: 'unknown', notes: '', baseline_seconds: 60,
  });

  // ===== DATA LOADING =====
  const load = useCallback(async () => {
    const [sr, dr, db] = await Promise.all([
      fetch(`${API}/api/test-lab/sessions?limit=50`, { headers: h }),
      fetch(`${API}/api/devices`, { headers: h }),
      fetch(`${API}/api/test-lab/dashboard`, { headers: h }),
    ]);
    if (sr.ok) setSessions(await sr.json());
    if (dr.ok) setDevices(await dr.json());
    if (db.ok) setDashboardData(await db.json());
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Find active session (but don't override guided mode)
  useEffect(() => {
    const active = sessions.find(s => ['recording', 'stopping'].includes(s.status));
    if (active) {
      setActiveSession(active);
      // Only auto-switch to live if NOT in guided mode and session is not guided
      if (view !== 'guided' && !active.guided) {
        setView('live');
      }
    } else {
      setActiveSession(null);
    }
  }, [sessions]);

  // Poll live status when recording
  useEffect(() => {
    if (!activeSession || activeSession.status !== 'recording') {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    const poll = async () => {
      try {
        const r = await fetch(`${API}/api/test-lab/sessions/${activeSession.id}/live-status`, { headers: h });
        if (r.ok) {
          const d = await r.json();
          setLiveStatus(d);
          if (d.latest_reading) {
            setChartData(prev => {
              const ts = d.latest_reading.timestamp?.slice(11, 19) || '';
              if (prev.length > 0 && prev[prev.length - 1].time === ts) return prev;
              return [...prev, {
                time: ts, PM1: d.latest_reading.pm1, 'PM2.5': d.latest_reading.pm2_5,
                PM10: d.latest_reading.pm10, VOC: d.latest_reading.voc,
                Fukt: d.latest_reading.humidity,
              }].slice(-180);
            });
          }
        }
      } catch { /* ignore */ }
    };
    poll();
    pollRef.current = setInterval(poll, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeSession?.id, activeSession?.status, token]);

  // Subscribe to device sensor readings via WebSocket
  useEffect(() => {
    if (!activeSession?.device_id) return;
    subscribeToDevice(activeSession.device_id);
    return () => unsubscribeFromDevice(activeSession.device_id);
  }, [activeSession?.device_id]);

  // ===== ACTIONS =====
  const runPreflight = async (deviceId) => {
    setPreflight(null);
    const r = await fetch(`${API}/api/test-lab/preflight/${deviceId}`, { headers: h });
    if (r.ok) setPreflight(await r.json());
  };

  const createAndStart = async () => {
    if (!form.device_id) { toast.error('Valj en sensor'); return; }
    const cr = await fetch(`${API}/api/test-lab/sessions`, {
      method: 'POST', headers: h, body: JSON.stringify(form),
    });
    if (!cr.ok) { toast.error('Kunde inte skapa session'); return; }
    const session = await cr.json();
    const sr = await fetch(`${API}/api/test-lab/sessions/${session.id}/start`, { method: 'POST', headers: h });
    if (sr.ok) {
      toast.success('Inspelning startad');
      setShowNewTest(false);
      setChartData([]);
      load();
    } else toast.error('Kunde inte starta inspelning');
  };

  const stopSession = async (id) => {
    if (!window.confirm('Avsluta testsession?')) return;
    setStopping(true);
    const r = await fetch(`${API}/api/test-lab/sessions/${id}/stop`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      toast.success(`Klar! ${d.readings_count} matpunkter sparade`);
      setChartData([]);
      setLiveStatus(null);
      setView('sessions');
      load();
    } else {
      const err = await r.json().catch(() => ({}));
      toast.error(err.detail || 'Stoppfel');
    }
    setStopping(false);
  };

  const abortSession = async (id) => {
    if (!window.confirm('Avbryt session? (data bevaras)')) return;
    const r = await fetch(`${API}/api/test-lab/sessions/${id}/abort`, { method: 'POST', headers: h });
    if (r.ok) { toast.success('Session avbruten'); setView('sessions'); load(); }
  };

  const deleteSession = async (id) => {
    if (!window.confirm('Radera session permanent?')) return;
    const r = await fetch(`${API}/api/test-lab/sessions/${id}`, { method: 'DELETE', headers: h });
    if (r.ok) { toast.success('Borttagen'); setSelectedSession(null); setView('sessions'); load(); }
    else toast.error('Kan inte radera aktiv session');
  };

  const addMarker = async (id, type, note = '') => {
    const r = await fetch(`${API}/api/test-lab/sessions/${id}/marker`, {
      method: 'POST', headers: h, body: JSON.stringify({ marker_type: type, note }),
    });
    if (r.ok) toast.success('Markering tillagd');
  };

  const setGroundTruth = async (id, label) => {
    await fetch(`${API}/api/test-lab/sessions/${id}/ground-truth`, {
      method: 'PUT', headers: h, body: JSON.stringify({ label, confidence: 'confirmed' }),
    });
    toast.success(`Facit: ${label}`);
    load();
  };

  const exportCsv = async (id) => {
    const r = await fetch(`${API}/api/test-lab/sessions/${id}/export`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `testlab-${id}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    }
  };

  const loadSessionDetail = async (id) => {
    const [sr, rr] = await Promise.all([
      fetch(`${API}/api/test-lab/sessions/${id}`, { headers: h }),
      fetch(`${API}/api/test-lab/sessions/${id}/readings?limit=5000`, { headers: h }),
    ]);
    if (sr.ok) setSelectedSession(await sr.json());
    if (rr.ok) {
      const readings = await rr.json();
      setDetailReadings(readings.map(r => ({
        time: r.timestamp?.slice(11, 19) || '',
        ts: r.timestamp,
        PM1: r.pm1, 'PM2.5': r.pm2_5, PM10: r.pm10,
        VOC: r.voc, Fukt: r.humidity, Temp: r.temperature, CO2: r.co2,
      })));
    }
    setReplayData(null);
    setView('detail');
  };

  const runReplay = async (id) => {
    setReplayLoading(true);
    const r = await fetch(`${API}/api/test-lab/sessions/${id}/replay`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      setReplayData(d);
      toast.success(`Replay klar — Score: ${d.peak_score?.toFixed(2)}, Klass: ${d.classification}`);
    } else toast.error('Replay misslyckades');
    setReplayLoading(false);
  };

  const loadValidation = async (split = validationSplit, source = validationSource) => {
    const params = new URLSearchParams({ source });
    if (split && split !== 'all') params.set('split', split);
    const r = await fetch(`${API}/api/test-lab/validation?${params}`, { headers: h });
    if (r.ok) setValidationData(await r.json());
    setView('validation');
  };

  const exportAllCsv = async (split) => {
    const q = split && split !== 'all' ? `?split=${split}` : '';
    const r = await fetch(`${API}/api/test-lab/sessions-export${q}`, { headers: h });
    if (!r.ok) { toast.error('Exporten misslyckades'); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = (r.headers.get('Content-Disposition') || '').match(/filename="(.+)"/)?.[1] || 'testlab-alla-tester.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const recomputeMetadata = async () => {
    toast.info('Räknar om baseline och larmkoppling...');
    const r = await fetch(`${API}/api/test-lab/sessions/recompute-metadata`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.updated} tester uppdaterade (inga data raderade)`);
      loadValidation();
    } else toast.error('Omräkning misslyckades');
  };

  const batchReplay = async () => {
    toast.info('Kor batch-replay...');
    const r = await fetch(`${API}/api/test-lab/sessions/batch-replay`, { method: 'POST', headers: h });
    if (r.ok) {
      const d = await r.json();
      toast.success(`${d.replayed} sessioner replayade med algo ${d.algorithm_version}`);
      loadValidation();
    }
  };

  // ===== GUIDED MODE =====
  if (view === 'guided') {
    return (
      <GuidedTestWizard
        devices={devices}
        onExit={() => { setView('sessions'); load(); }}
        onComplete={() => { setView('sessions'); load(); }}
      />
    );
  }


  // ===== LIVE RECORDING VIEW =====
  if (view === 'live' && activeSession) {
    const ls = liveStatus;
    const markers = ls?.event_markers || [];
    return (
      <div className="space-y-4" data-testid="testlab-live">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="h-3 w-3 rounded-full bg-red-500" />
              <div className="absolute inset-0 h-3 w-3 rounded-full bg-red-500 animate-ping opacity-75" />
            </div>
            <div>
              <h2 className="text-lg font-bold tracking-tight">LIVE — {activeSession.name}</h2>
              <p className="text-xs text-muted-foreground">{activeSession.device_name} | {activeSession.category}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="destructive" size="sm" onClick={() => stopSession(activeSession.id)} disabled={stopping} data-testid="stop-btn">
              {stopping ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Square className="h-3.5 w-3.5 mr-1" />} Stoppa
            </Button>
            <Button variant="outline" size="sm" onClick={() => abortSession(activeSession.id)} data-testid="abort-btn">Avbryt</Button>
          </div>
        </div>

        {/* Status bar */}
        <div className="flex items-center gap-4 text-xs rounded-lg border bg-card/50 px-4 py-2.5">
          <span className="flex items-center gap-1.5">
            {ls?.latest_reading ? <Wifi className="h-3.5 w-3.5 text-emerald-500" /> : <WifiOff className="h-3.5 w-3.5 text-red-500" />}
            {ls?.latest_reading ? 'ONLINE' : 'VANTAR PA DATA...'}
          </span>
          <span className="text-muted-foreground">|</span>
          <span>Matpunkter: <strong className="font-mono">{ls?.readings_count || 0}</strong></span>
          <span className="text-muted-foreground">|</span>
          <span>Fas: <strong>{ls?.phase || 'baseline'}</strong></span>
          {ls?.is_stale && <Badge className="bg-amber-100 text-amber-700 ml-auto">INAKTUELL</Badge>}
        </div>

        {/* Live value cards */}
        {ls?.latest_reading && (
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[['PM1', ls.latest_reading.pm1, 'ug/m3', '#3b82f6'],
              ['PM2.5', ls.latest_reading.pm2_5, 'ug/m3', '#f59e0b'],
              ['PM10', ls.latest_reading.pm10, 'ug/m3', '#ef4444'],
              ['VOC', ls.latest_reading.voc, 'index', '#a855f7'],
              ['Fukt', ls.latest_reading.humidity, '%', '#06b6d4'],
            ].map(([label, val, unit, color]) => (
              <div key={label} className="rounded-lg border bg-card p-3 text-center">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
                <div className="text-2xl font-bold font-mono" style={{ color: val != null ? color : undefined }}>
                  {val != null ? (typeof val === 'number' ? val.toFixed(1) : val) : '—'}
                </div>
                <div className="text-[9px] text-muted-foreground">{unit}</div>
              </div>
            ))}
          </div>
        )}

        {/* Live chart */}
        {chartData.length > 1 && (
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">Realtidsdata</CardTitle>
            </CardHeader>
            <CardContent className="pb-2 px-2">
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                  <XAxis dataKey="time" tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="pm" tick={{ fontSize: 9 }} label={{ value: 'PM (ug/m3)', angle: -90, position: 'insideLeft', style: { fontSize: 9 } }} />
                  <YAxis yAxisId="voc" orientation="right" tick={{ fontSize: 9 }} label={{ value: 'VOC', angle: 90, position: 'insideRight', style: { fontSize: 9 } }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line yAxisId="pm" type="monotone" dataKey="PM1" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                  <Line yAxisId="pm" type="monotone" dataKey="PM2.5" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <Line yAxisId="pm" type="monotone" dataKey="PM10" stroke="#ef4444" strokeWidth={1.5} dot={false} />
                  <Line yAxisId="voc" type="monotone" dataKey="VOC" stroke="#a855f7" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                  {markers.map((m, i) => (
                    <ReferenceLine key={i} yAxisId="pm" x={m.timestamp?.slice(11, 19)}
                      stroke={MARKER_TYPES.find(mt => mt.value === m.type)?.color || '#666'}
                      strokeDasharray="3 3" strokeWidth={2}
                      label={{ value: m.type.replace('_now', '').replace('_', ' '), position: 'top', fontSize: 8 }} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Event markers */}
        <Card>
          <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Markera handelse</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {MARKER_TYPES.map(m => (
                <Button key={m.value} size="sm" variant="outline"
                  onClick={() => addMarker(activeSession.id, m.value)}
                  style={{ borderColor: m.color + '44' }}
                  className="hover:opacity-80 transition-opacity"
                  data-testid={`marker-${m.value}`}>
                  <span className="inline-block w-4 h-4 rounded text-[10px] text-white font-bold flex items-center justify-center mr-1.5"
                    style={{ backgroundColor: m.color }}>{m.icon}</span>
                  {m.label}
                </Button>
              ))}
            </div>
            {markers.length > 0 && (
              <div className="mt-3 space-y-1 max-h-32 overflow-y-auto">
                {markers.map((m, i) => (
                  <div key={i} className="text-xs flex gap-2 items-center">
                    <span className="text-muted-foreground font-mono w-16">{m.timestamp?.slice(11, 19)}</span>
                    <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: MARKER_TYPES.find(mt => mt.value === m.type)?.color || '#666' }} />
                    <span className="font-medium">{m.type.replace(/_/g, ' ')}</span>
                    {m.note && <span className="text-muted-foreground">— {m.note}</span>}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Diagnostics */}
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground transition-colors">Diagnostik</summary>
          <pre className="mt-1 bg-muted/50 p-2 rounded text-[10px] overflow-auto font-mono">
{JSON.stringify({ session_id: activeSession.id, device_id: activeSession.device_id, status: ls?.status,
  readings_backend: ls?.readings_count, is_stale: ls?.is_stale, phase: ls?.phase, algorithm: activeSession.algorithm_version }, null, 2)}
          </pre>
        </details>
      </div>
    );
  }

  // ===== SESSION DETAIL VIEW =====
  if (view === 'detail' && selectedSession) {
    const s = selectedSession;
    const st = STATUS_MAP[s.status] || STATUS_MAP.created;
    const gtLabel = GROUND_TRUTH_LABELS.find(g => g.value === s.label);

    // Merge replay scores onto readings chart
    const chartWithReplay = detailReadings.map(r => {
      const replayPoint = replayData?.timeline?.find(t => t.timestamp?.slice(11, 19) === r.time);
      return { ...r, Score: replayPoint?.score ?? null };
    });

    return (
      <div className="space-y-4" data-testid="testlab-detail">
        <Button variant="ghost" size="sm" onClick={() => { setView('sessions'); setSelectedSession(null); }} data-testid="back-btn">
          &larr; Tillbaka
        </Button>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight">{s.name}</h2>
            <p className="text-xs text-muted-foreground">{s.device_name} — {s.created_at?.slice(0, 16).replace('T', ' ')}</p>
          </div>
          <div className="flex items-center gap-2">
            {gtLabel && (
              <Badge style={{ backgroundColor: gtLabel.color + '22', color: gtLabel.color, borderColor: gtLabel.color + '44' }} className="border">
                Facit: {gtLabel.label}
              </Badge>
            )}
            <Badge className={st.cls}>{st.label}</Badge>
          </div>
        </div>

        {/* Result summary cards */}
        {s.status === 'completed' && s.computed && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {[['Matpunkter', s.readings_count, '#3b82f6'],
              ['Duration', `${s.computed.duration_s}s`, '#6b7280'],
              ['Peak PM2.5', s.computed.peak_pm25?.toFixed(1), '#f59e0b'],
              ['Delta PM2.5', s.computed.pm25_delta?.toFixed(1), '#ef4444'],
              ['PM1/PM2.5', s.computed.pm1_pm25_ratio?.toFixed(3), '#a855f7'],
            ].map(([l, v, c]) => (
              <div key={l} className="rounded-lg border p-3 text-center">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{l}</div>
                <div className="text-xl font-bold font-mono" style={{ color: c }}>{v ?? '—'}</div>
              </div>
            ))}
          </div>
        )}

        {/* Ground truth + data quality + actions */}
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={s.label || 'unknown'} onValueChange={v => { setGroundTruth(s.id, v); setSelectedSession({ ...s, label: v }); }}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="ground-truth-select">
              <Tag className="h-3 w-3 mr-1" /><SelectValue placeholder="Satt facit" />
            </SelectTrigger>
            <SelectContent>
              {GROUND_TRUTH_LABELS.map(g => (
                <SelectItem key={g.value} value={g.value}>
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: g.color }} />
                    {g.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {s.data_quality && (
            <Badge className={s.data_quality.status === 'valid' ? 'bg-emerald-100 text-emerald-700' :
              s.data_quality.status === 'questionable' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
              Kvalitet: {s.data_quality.status}
            </Badge>
          )}

          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" onClick={() => exportCsv(s.id)} data-testid="export-csv-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> CSV
            </Button>
            {s.status === 'completed' && (
              <Button size="sm" variant="outline" onClick={() => runReplay(s.id)} disabled={replayLoading} data-testid="replay-btn">
                {replayLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />}
                Replay
              </Button>
            )}
            <Button size="sm" variant="destructive" onClick={() => deleteSession(s.id)} data-testid="delete-session-btn">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Readings chart with optional replay overlay */}
        {detailReadings.length > 1 && (
          <Card>
            <CardHeader className="pb-1 pt-3 px-4">
              <CardTitle className="text-xs text-muted-foreground uppercase tracking-wider">
                Sensordata {replayData ? '+ Replay-score' : ''}
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-2 px-2">
              <ResponsiveContainer width="100%" height={320}>
                <ComposedChart data={chartWithReplay}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                  <XAxis dataKey="time" tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="pm" tick={{ fontSize: 9 }} />
                  <YAxis yAxisId="score" orientation="right" domain={[0, 1]} tick={{ fontSize: 9 }}
                    label={{ value: 'Score', angle: 90, position: 'insideRight', style: { fontSize: 9 } }} />
                  <Tooltip contentStyle={{ fontSize: 11 }} />
                  <Legend wrapperStyle={{ fontSize: 10 }} />
                  <Line yAxisId="pm" type="monotone" dataKey="PM1" stroke="#3b82f6" strokeWidth={1.5} dot={false} />
                  <Line yAxisId="pm" type="monotone" dataKey="PM2.5" stroke="#f59e0b" strokeWidth={2} dot={false} />
                  <Line yAxisId="pm" type="monotone" dataKey="PM10" stroke="#ef4444" strokeWidth={1.5} dot={false} />
                  <Line yAxisId="pm" type="monotone" dataKey="VOC" stroke="#a855f7" strokeWidth={1} dot={false} strokeDasharray="4 2" />
                  {replayData && (
                    <Area yAxisId="score" type="monotone" dataKey="Score" fill="#22c55e33" stroke="#22c55e" strokeWidth={2} dot={false} />
                  )}
                  {/* Event markers as reference lines */}
                  {(s.event_markers || []).map((m, i) => (
                    <ReferenceLine key={i} yAxisId="pm" x={m.timestamp?.slice(11, 19)}
                      stroke={MARKER_TYPES.find(mt => mt.value === m.type)?.color || '#666'}
                      strokeDasharray="3 3" strokeWidth={2} />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Replay result panel */}
        {replayData && (
          <Card className="border-emerald-200 bg-emerald-50/30 dark:bg-emerald-950/10">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-emerald-600" />
                Replay-resultat (Algo {replayData.algorithm_version})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div><span className="text-muted-foreground text-xs">Klassificering</span><br /><strong className="text-base">{replayData.classification}</strong></div>
                <div><span className="text-muted-foreground text-xs">Peak score</span><br /><strong className="text-base font-mono">{replayData.peak_score?.toFixed(3)}</strong></div>
                <div><span className="text-muted-foreground text-xs">Vape-readings</span><br /><strong className="text-base font-mono">{replayData.vape_readings}</strong></div>
                <div><span className="text-muted-foreground text-xs">Suspect-readings</span><br /><strong className="text-base font-mono">{replayData.suspect_readings}</strong></div>
                <div><span className="text-muted-foreground text-xs">Analyserade</span><br /><strong className="text-base font-mono">{replayData.readings_analyzed}</strong></div>
              </div>
              {replayData.original_label && replayData.original_label !== 'unknown' && (
                <div className="mt-2 text-xs">
                  <span className="text-muted-foreground">Facit: </span>
                  <strong>{replayData.original_label}</strong>
                  {' — '}
                  {replayData.classification === replayData.original_label ? (
                    <span className="text-emerald-600 font-semibold">KORREKT</span>
                  ) : (
                    <span className="text-red-600 font-semibold">FEL</span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Event markers list */}
        {s.event_markers?.length > 0 && (
          <Card>
            <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Handelsemarkeringar ({s.event_markers.length})</CardTitle></CardHeader>
            <CardContent className="space-y-1">
              {s.event_markers.map((m, i) => (
                <div key={i} className="text-xs flex gap-2 items-center">
                  <span className="text-muted-foreground font-mono w-16">{m.timestamp?.slice(11, 19)}</span>
                  <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: MARKER_TYPES.find(mt => mt.value === m.type)?.color || '#666' }} />
                  <span className="font-medium">{m.type.replace(/_/g, ' ')}</span>
                  {m.note && <span className="text-muted-foreground">— {m.note}</span>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Test params */}
        <Card>
          <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Testparametrar</CardTitle></CardHeader>
          <CardContent className="text-xs grid grid-cols-2 md:grid-cols-4 gap-y-1.5 gap-x-4">
            {Object.entries(s.test_params || {}).filter(([, v]) => v && v !== 'unknown').map(([k, v]) => (
              <div key={k}><span className="text-muted-foreground">{k}:</span> <strong>{String(v)}</strong></div>
            ))}
          </CardContent>
        </Card>

        {/* Audit log */}
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer hover:text-foreground transition-colors">Audit-logg ({(s.audit_log || []).length})</summary>
          <div className="mt-1 space-y-0.5 font-mono text-[10px]">
            {(s.audit_log || []).map((a, i) => (
              <div key={i}>{a.timestamp?.slice(0, 19)} — {a.action} {a.user ? `(${a.user.slice(0, 8)})` : ''} {a.label || ''}</div>
            ))}
          </div>
        </details>
      </div>
    );
  }

  // ===== ANALYSIS VIEW =====
  if (view === 'analysis') {
    return <TestLabAnalysis headers={h} onBack={() => setView('sessions')} />;
  }

  // ===== VALIDATION VIEW =====
  if (view === 'validation') {
    const v = validationData;
    return (
      <div className="space-y-4" data-testid="testlab-validation">
        <Button variant="ghost" size="sm" onClick={() => setView('sessions')}>
          &larr; Tillbaka
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold tracking-tight flex items-center gap-2">
              <Target className="h-5 w-5 text-emerald-600" /> Validering
            </h2>
            <p className="text-xs text-muted-foreground">Algoritmens precision mot facit (ground truth)</p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => exportAllCsv(validationSplit)} data-testid="export-all-csv-validation-btn">
              <Download className="h-3.5 w-3.5 mr-1" /> Exportera CSV
            </Button>
            <Button size="sm" variant="outline" onClick={recomputeMetadata} data-testid="recompute-metadata-btn">
              Räkna om baseline &amp; larm
            </Button>
            <Button size="sm" variant="outline" onClick={batchReplay} data-testid="batch-replay-btn">
              <RefreshCw className="h-3.5 w-3.5 mr-1" /> Replaya alla
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="validation-source-filter">
          <span className="text-xs text-muted-foreground">Källa:</span>
          {[['live', 'Riktiga systemets beslut'], ['replay', 'Förenklad replay']].map(([key, label]) => (
            <Button key={key} size="sm" variant={validationSource === key ? 'default' : 'outline'} className="h-7 text-xs"
              onClick={() => { setValidationSource(key); loadValidation(validationSplit, key); }} data-testid={`source-${key}`}>
              {label}
            </Button>
          ))}
          {v?.excluded_without_data > 0 && (
            <span className="text-[11px] text-amber-600" data-testid="validation-excluded">
              {v.excluded_without_data} test saknar data för vald källa och räknas inte
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2" data-testid="validation-split-filter">
          <span className="text-xs text-muted-foreground">Datamängd:</span>
          {[['all', 'Alla'], ['train', 'Träning'], ['validation', 'Validering'], ['test', 'Test']].map(([key, label]) => (
            <Button key={key} size="sm" variant={validationSplit === key ? 'default' : 'outline'} className="h-7 text-xs"
              onClick={() => { setValidationSplit(key); loadValidation(key); }} data-testid={`split-${key}`}>
              {label}{key !== 'all' && v?.split_counts ? ` (${v.split_counts[key] ?? 0} test, ${v.split_groups?.[key] ?? 0} serier)` : ''}
            </Button>
          ))}
          <span className="text-[11px] text-muted-foreground">Fast fördelning per testvariant: alla grupper får vape, spray, blandade och normala kontroller. Upprepningar av samma variant (eller samma serie-ID) hålls i samma grupp. Utvärdera slutligt på Test.</span>
        </div>

        {!v ? (
          <div className="text-center py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 mx-auto animate-spin mb-3 opacity-30" />
            <p className="text-sm">Laddar valideringsdata...</p>
          </div>
        ) : v.total_labeled === 0 ? (
          <Card className="p-8 text-center">
            <Target className="h-10 w-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm text-muted-foreground">Inga sessioner med facit och replay annu.</p>
            <p className="text-xs text-muted-foreground mt-1">Kor tester, satt facit (ground truth), och replaya for att se statistik.</p>
          </Card>
        ) : (
          <>
            {/* Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[['Accuracy', v.metrics.accuracy, '#3b82f6'],
                ['Precision', v.metrics.precision, '#22c55e'],
                ['Recall', v.metrics.recall, '#f59e0b'],
                ['F1-score', v.metrics.f1_score, '#a855f7'],
              ].map(([label, val, color]) => (
                <Card key={label} className="text-center p-4">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
                  <div className="text-3xl font-bold font-mono mt-1" style={{ color }}>{(val * 100).toFixed(1)}%</div>
                </Card>
              ))}
            </div>

            {/* Confusion matrix */}
            <Card>
              <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Forvirringsmatris</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-1 max-w-xs mx-auto text-center text-sm">
                  <div /> <div className="text-xs text-muted-foreground font-semibold pb-1">Algo: Pos</div> <div className="text-xs text-muted-foreground font-semibold pb-1">Algo: Neg</div>
                  <div className="text-xs text-muted-foreground font-semibold text-right pr-2">Facit: Pos</div>
                  <div className="rounded-lg p-3 font-bold font-mono text-xl" style={{ backgroundColor: '#22c55e22', color: '#22c55e' }}>{v.confusion_matrix.tp}</div>
                  <div className="rounded-lg p-3 font-bold font-mono text-xl" style={{ backgroundColor: '#f59e0b22', color: '#f59e0b' }}>{v.confusion_matrix.fn}</div>
                  <div className="text-xs text-muted-foreground font-semibold text-right pr-2">Facit: Neg</div>
                  <div className="rounded-lg p-3 font-bold font-mono text-xl" style={{ backgroundColor: '#ef444422', color: '#ef4444' }}>{v.confusion_matrix.fp}</div>
                  <div className="rounded-lg p-3 font-bold font-mono text-xl" style={{ backgroundColor: '#3b82f622', color: '#3b82f6' }}>{v.confusion_matrix.tn}</div>
                </div>
                <div className="text-center text-[10px] text-muted-foreground mt-2">
                  TP = Korrekt vape | FP = Falskt alarm | TN = Korrekt normal | FN = Missad vape
                </div>
              </CardContent>
            </Card>

            {/* Per-session results */}
            <Card>
              <CardHeader className="pb-2 pt-3"><CardTitle className="text-sm">Sessioner ({v.results.length})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1 max-h-96 overflow-y-auto">
                  {v.results.map(r => (
                    <div key={r.id} className="flex items-center gap-3 text-xs py-1.5 border-b last:border-0 cursor-pointer hover:bg-muted/50 rounded px-1"
                      onClick={() => loadSessionDetail(r.id)}>
                      <Badge style={{ backgroundColor: VERDICT_COLORS[r.verdict] + '22', color: VERDICT_COLORS[r.verdict], borderColor: VERDICT_COLORS[r.verdict] }}
                        className="border w-10 justify-center font-mono text-[10px]">{r.verdict}</Badge>
                      <span className="flex-1 min-w-0 truncate font-medium">{r.name}</span>
                      <span className="text-muted-foreground">Facit: {r.ground_truth}</span>
                      <span className="text-muted-foreground">Algo: {r.algo_classification}</span>
                      <span className="font-mono w-12 text-right">{r.algo_score?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  }

  // ===== MAIN SESSION LIST VIEW =====
  return (
    <div className="space-y-5" data-testid="test-lab-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-white" />
            </div>
            Testlabb
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5 ml-[46px]">Test, validering och ground truth</p>
        </div>
        <div className="flex gap-2">
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setView('guided')} data-testid="guided-test-btn">
            <Play className="h-4 w-4 mr-1" /> Guidat test
          </Button>
          <Button variant="outline" size="sm" onClick={() => loadValidation()} data-testid="validation-btn">
            <Target className="h-3.5 w-3.5 mr-1" /> Validering
          </Button>
          <Button variant="outline" size="sm" onClick={() => setView('analysis')} data-testid="analysis-btn">
            <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Falsklarm &amp; analys
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportAllCsv()} data-testid="export-all-csv-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Exportera alla
          </Button>
          <Button variant="outline" onClick={() => setShowNewTest(true)} data-testid="new-test-btn">
            <Plus className="h-4 w-4 mr-1" /> Avancerat
          </Button>
        </div>
      </div>

      {/* Quick stats */}
      {dashboardData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-3 text-center">
            <div className="text-[10px] text-muted-foreground uppercase">Totalt</div>
            <div className="text-2xl font-bold font-mono">{dashboardData.total_sessions}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-[10px] text-muted-foreground uppercase">Giltiga</div>
            <div className="text-2xl font-bold font-mono text-emerald-600">{dashboardData.valid_tests}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-[10px] text-muted-foreground uppercase">Vape-tester</div>
            <div className="text-2xl font-bold font-mono text-red-600">{dashboardData.labels?.vape || 0}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-[10px] text-muted-foreground uppercase">Spray-tester</div>
            <div className="text-2xl font-bold font-mono text-amber-600">{dashboardData.labels?.spray || 0}</div>
          </Card>
        </div>
      )}

      {/* Zombie/stale session warnings */}
      {sessions.filter(s => s.status === 'recording' || s.status === 'failed').map(s => (
        <div key={s.id} className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 flex items-center justify-between">
          <div className="text-sm">
            <AlertTriangle className="h-4 w-4 inline mr-1.5 text-amber-600" />
            {s.status === 'failed' ? 'Misslyckad session' : 'Aktiv inspelning'}: <strong>{s.name}</strong>
          </div>
          <div className="flex gap-2">
            {s.status === 'recording' && (
              <>
                <Button size="sm" variant="outline" onClick={() => { setActiveSession(s); setView('live'); }}>Visa</Button>
                <Button size="sm" variant="outline" onClick={() => stopSession(s.id)}>Stoppa</Button>
              </>
            )}
            <Button size="sm" variant="outline" onClick={() => abortSession(s.id)}>Avbryt</Button>
          </div>
        </div>
      ))}

      {/* Session list */}
      <div className="space-y-2">
        {sessions.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <FlaskConical className="h-12 w-12 mx-auto mb-3 opacity-15" />
            <p className="text-sm font-medium">Inga testsessioner annu</p>
            <p className="text-xs mt-1">Skapa ditt forsta test for att borja validera detektionsalgoritmen.</p>
          </div>
        ) : sessions.map(s => {
          const st = STATUS_MAP[s.status] || STATUS_MAP.created;
          const gtLabel = GROUND_TRUTH_LABELS.find(g => g.value === s.label);
          return (
            <div key={s.id}
              className="group rounded-xl border bg-card p-4 hover:shadow-md transition-all cursor-pointer flex items-center gap-4"
              onClick={() => loadSessionDetail(s.id)} data-testid={`session-${s.id}`}>
              <Badge className={`${st.cls} text-[10px] w-20 justify-center`}>{st.label}</Badge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{s.name}</div>
                <div className="text-[10px] text-muted-foreground">{s.device_name} — {s.created_at?.slice(0, 16).replace('T', ' ')}</div>
              </div>
              <div className="text-xs text-right space-y-0.5">
                <div className="font-mono">{s.readings_count || 0} mpt</div>
                {gtLabel && gtLabel.value !== 'unknown' && (
                  <Badge variant="outline" className="text-[9px]" style={{ borderColor: gtLabel.color, color: gtLabel.color }}>
                    {gtLabel.label}
                  </Badge>
                )}
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); exportCsv(s.id); }}>
                  <Download className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => { e.stopPropagation(); deleteSession(s.id); }}>
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* New Test Dialog */}
      <Dialog open={showNewTest} onOpenChange={setShowNewTest}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nytt test</DialogTitle><DialogDescription>Konfigurera och starta en ny testsession</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Sensor</label>
              <Select value={form.device_id} onValueChange={v => { setForm({ ...form, device_id: v }); runPreflight(v); }}>
                <SelectTrigger data-testid="select-sensor"><Radio className="h-3.5 w-3.5 mr-1" /><SelectValue placeholder="Valj sensor" /></SelectTrigger>
                <SelectContent>{devices.map(d => <SelectItem key={d.id} value={d.id}>{d.name || d.device_eui}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            {/* Preflight result */}
            {preflight && (
              <div className={`rounded-lg border p-3 text-xs ${preflight.is_online ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20' : 'border-amber-300 bg-amber-50 dark:bg-amber-950/20'}`}>
                <div className="flex items-center gap-2 font-medium">
                  {preflight.is_online ? <CheckCircle className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  {preflight.device_name} — {preflight.is_online ? 'ONLINE' : 'VANTAR PA DATA'}
                </div>
                <div className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-0.5 text-muted-foreground">
                  <span>Senaste: {preflight.data_age_seconds != null ? (preflight.data_age_seconds < 60 ? `${preflight.data_age_seconds}s sedan` : `${Math.round(preflight.data_age_seconds / 60)} min sedan`) : 'Aldrig'}</span>
                  {preflight.frequency_seconds && <span>Frekvens: ~{preflight.frequency_seconds}s</span>}
                  {preflight.last_reading && <span>PM2.5: {preflight.last_reading.pm2_5}</span>}
                  {preflight.room_name && <span>Rum: {preflight.room_name}</span>}
                </div>
                {preflight.active_session && <div className="mt-1 text-amber-600 font-medium">Aktiv session pagar redan ({preflight.active_session.status})</div>}
                {!preflight.is_online && preflight.data_age_seconds != null && (
                  <div className="mt-1 text-amber-700">Sensorn har inte skickat data pa {Math.round((preflight.data_age_seconds || 0) / 60)} min. Du kan starta testet anda — matpunkter sparas nar data borjar komma.</div>
                )}
                {!preflight.is_online && preflight.data_age_seconds == null && (
                  <div className="mt-1 text-amber-700">Ingen data hittad for denna sensor. Kontrollera att sensorn ar konfigurerad korrekt.</div>
                )}
              </div>
            )}

            <Input placeholder="Testnamn (valfritt)" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="test-name-input" />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Kategori</label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Avstand (m)</label>
                <Select value={String(form.distance_m)} onValueChange={v => setForm({ ...form, distance_m: parseFloat(v) })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['0.5','1','1.5','2','3'].map(d => <SelectItem key={d} value={d}>{d} m</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-medium">Ventilation</label>
                <Select value={form.ventilation} onValueChange={v => setForm({ ...form, ventilation: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{['off','low','normal','high','unknown'].map(v => <SelectItem key={v} value={v}>{v === 'unknown' ? 'Okand' : v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Dorr</label>
                <Select value={form.door} onValueChange={v => setForm({ ...form, door: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Oppen</SelectItem>
                    <SelectItem value="closed">Stangd</SelectItem>
                    <SelectItem value="unknown">Okand</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Input placeholder="Substans / produkt" value={form.substance} onChange={e => setForm({ ...form, substance: e.target.value })} data-testid="substance-input" />
            <Textarea placeholder="Anteckningar" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="notes-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTest(false)}>Avbryt</Button>
            <Button onClick={createAndStart} disabled={!form.device_id || (preflight?.active_session)} data-testid="start-test-btn">
              <Play className="h-4 w-4 mr-1" /> Starta test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
